import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
// Import Store
import { useAppSelector } from 'store';
import { selectPartnerSettings } from 'store/slices/partnerSettings';
// Import Hooks
import useBind from 'hooks/UseBind';
import useInputInsertion from 'hooks/UseInputInsertion';
// Import UI Components
import { Section } from '@geneui/components';
// Import Components
import ViberTemplateContent from './ViberTemplateContent';
import SMSContent from './SMSContent';
import TemplateCategoryInput from './TemplateCategoryInput';
// Import Enums
import { TemplateTypeEnum } from 'enums';
// Import Constants
import { l, TemplateInputTypes } from 'constants/common';
import { DefaultLanguage } from 'constants/template';
import DynamicVariables from './DynamicVariables';

const ViberCascadeContent = ({
    templateData,
    templateLangKey,
    handleChange,
    maxSMSPartsCount,
    shortenerAndTrackerSettings,
    dynamicVariables,
}) => {
    const { t } = useTranslation();

    const { IsAllowViberSmsCascading: isCascade } = useAppSelector(selectPartnerSettings) ?? {};
    const { insertionHandler, updateInsertionHandler, insertionOptions, setInsertionOptions } = useInputInsertion();

    const categoryValue = templateData?.[TemplateInputTypes.Category];

    const handleCategoryChange = useBind(handleChange, TemplateInputTypes.Category);
    const updateDynamicVariableOptionsViber = useCallback(
        ({ hintText, focusedInputKey }) => {
            setInsertionOptions({ channel: TemplateTypeEnum.Viber, inputFilterKey: '', hintText, focusedInputKey });
        },
        [setInsertionOptions],
    );

    const updateDynamicVariableOptionsSMS = useCallback(() => {
        setInsertionOptions({
            channel: TemplateTypeEnum.SMS,
            inputFilterKey: '',
            hintText: '',
            focusedInputKey: TemplateInputTypes.SMSBody,
        });
    }, [setInsertionOptions]);

    return (
        <>
            <DynamicVariables
                activeChannel=""
                dynamicVariables={dynamicVariables}
                clickHandler={insertionHandler}
                insertionOptions={insertionOptions}
            />
            <div className="crm-create-template-page-c-c-h-inputs-row">
                {templateLangKey === DefaultLanguage && (
                    <TemplateCategoryInput value={categoryValue} onChange={handleCategoryChange} />
                )}
            </div>

            {!isCascade ? (
                <ViberTemplateContent
                    templateData={templateData}
                    templateLangKey={templateLangKey}
                    dynamicVariables={dynamicVariables}
                    handleChange={handleChange}
                    shortenerAndTrackerSettings={shortenerAndTrackerSettings.viber}
                    updateDynamicVariableOptions={updateDynamicVariableOptionsViber}
                    updateInsertionHandler={updateInsertionHandler}
                />
            ) : (
                <div className="viber-template-cascade-container">
                    <div className="viber-template-cascade-section viber-content">
                        <Section text={t(l.ViberContent)}>
                            <div className="viber-template-cascade-section-content">
                                <ViberTemplateContent
                                    templateData={templateData}
                                    templateLangKey={templateLangKey}
                                    dynamicVariables={dynamicVariables}
                                    handleChange={handleChange}
                                    shortenerAndTrackerSettings={shortenerAndTrackerSettings.viber}
                                    updateDynamicVariableOptions={updateDynamicVariableOptionsViber}
                                    updateInsertionHandler={updateInsertionHandler}
                                />
                            </div>
                        </Section>
                    </div>
                    <div className="viber-template-cascade-section sms-content">
                        <Section text={t(l.SMSContent)}>
                            <div className="viber-template-cascade-section-content">
                                <SMSContent
                                    bodyPropertyName={TemplateInputTypes.SMSBody}
                                    templateData={templateData}
                                    templateLangKey={templateLangKey}
                                    maxSMSPartsCount={maxSMSPartsCount}
                                    handleChange={handleChange}
                                    shortenerAndTrackerSettings={shortenerAndTrackerSettings.sms}
                                    updateDynamicVariableOptions={updateDynamicVariableOptionsSMS}
                                    updateInsertionHandler={updateInsertionHandler}
                                    shouldFocusOnMount={false}
                                />
                            </div>
                        </Section>
                    </div>
                </div>
            )}
        </>
    );
};

ViberCascadeContent.propTypes = {
    templateData: PropTypes.object.isRequired,
    templateLangKey: PropTypes.string.isRequired,
    bodyValidationState: PropTypes.object,
    maxSMSPartsCount: PropTypes.number.isRequired,
    handleChange: PropTypes.func.isRequired,
    updateFocusedInput: PropTypes.func,
    shortenerAndTrackerSettings: PropTypes.object.isRequired,
    dynamicVariables: PropTypes.arrayOf(PropTypes.object),
};

export default ViberCascadeContent;
